import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { AlertTriangle, ArrowLeft, Loader2, Utensils, Info, Share2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import ShareReportDialog from "@/components/ShareReportDialog";

const SEVERITY_ORDER = { severe: 0, moderate: 1, mild: 2 };

function ScoreDial({ score }) {
  const s = Math.max(0, Math.min(100, Number(score) || 0));
  const color = s >= 80 ? "#16A34A" : s >= 60 ? "#D97706" : "#DC2626";
  const dash = (s / 100) * 283;
  return (
    <div className="relative w-32 h-32">
      <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
        <circle cx="50" cy="50" r="45" fill="none" stroke="#F4EFE6" strokeWidth="8" />
        <circle
          cx="50"
          cy="50"
          r="45"
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeLinecap="round"
          strokeDasharray={`${dash} 283`}
          style={{ transition: "stroke-dasharray 700ms ease" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <div className="text-center">
          <p className="font-serif-display text-3xl font-semibold" style={{ color: "#1C2826" }}>
            {s}
          </p>
          <p className="text-[10px] uppercase tracking-widest" style={{ color: "#5C6B66" }}>
            harmony
          </p>
        </div>
      </div>
    </div>
  );
}

function SeverityBadge({ level }) {
  const cls =
    level === "severe"
      ? "severity-severe"
      : level === "moderate"
      ? "severity-moderate"
      : "severity-mild";
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-bold border ${cls}`}
      data-testid={`severity-badge-${level}`}
    >
      {level}
    </span>
  );
}

export default function ReportView() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reports/${id}`);
        setReport(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center">
        <Loader2 className="animate-spin mx-auto" style={{ color: "#2D5A4C" }} />
      </div>
    );
  }
  if (!report) {
    return (
      <div className="max-w-3xl mx-auto py-24 text-center">
        <p style={{ color: "#5C6B66" }}>Report not found.</p>
      </div>
    );
  }

  const s = report.summary || {};
  const interactions = (s.interactions || []).slice().sort(
    (a, b) => (SEVERITY_ORDER[a.severity] ?? 3) - (SEVERITY_ORDER[b.severity] ?? 3)
  );
  const filtered =
    tab === "all"
      ? interactions
      : tab === "food"
      ? interactions.filter((i) => ["drug-food", "drug-alcohol", "drug-lifestyle"].includes(i.type))
      : interactions.filter((i) => i.severity === tab);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <Link to="/dashboard" data-testid="back-to-dashboard-link" className="inline-flex items-center gap-1 text-sm mb-6" style={{ color: "#5C6B66" }}>
        <ArrowLeft size={14} /> Back to dashboard
      </Link>

      <div className="flex justify-end mb-4 print:hidden">
        <Button
          data-testid="open-share-dialog-btn"
          onClick={() => setShareOpen(true)}
          className="btn-terracotta rounded-full h-10"
        >
          <Share2 size={14} className="mr-2" /> Share with doctor
        </Button>
      </div>
      <ShareReportDialog open={shareOpen} onOpenChange={setShareOpen} reportId={id} />

      <div className="grid lg:grid-cols-3 gap-6">
        {/* SUMMARY CARD */}
        <div className="lg:col-span-1 card-warm p-6 flex flex-col items-center">
          <ScoreDial score={s.harmony_score} />
          <p className="mt-4 text-center text-sm leading-relaxed" style={{ color: "#5C6B66" }}>
            {s.overall_summary}
          </p>
          <div className="mt-5 w-full space-y-2 text-xs" style={{ color: "#5C6B66" }}>
            <div className="flex justify-between border-b pb-1" style={{ borderColor: "#E5DEC9" }}>
              <span>Generated</span>
              <span>{new Date(report.created_at).toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span>Items analyzed</span>
              <span>{report.medications?.length ?? 0}</span>
            </div>
          </div>

          <div
            className="mt-5 w-full rounded-xl p-3 text-[11px] leading-relaxed"
            style={{ background: "#FDF0EA", border: "1px solid #E5DEC9", color: "#5C3B2A" }}
            data-testid="report-disclaimer-notice"
          >
            <div className="flex gap-2 items-start">
              <AlertTriangle size={14} style={{ color: "#BF5832" }} className="mt-0.5 shrink-0" />
              <span>
                <strong>Medical notice:</strong> This report is informational only. Discuss all
                changes with your doctor or pharmacist.
              </span>
            </div>
          </div>
        </div>

        {/* DETAIL COLUMN */}
        <div className="lg:col-span-2 space-y-6">
          <div className="card-warm p-6">
            <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>
              Cross-interactions
            </h2>

            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="rounded-full bg-[#F4EFE6] p-1 flex-wrap h-auto">
                {[
                  { v: "all", l: "All" },
                  { v: "severe", l: "Severe" },
                  { v: "moderate", l: "Moderate" },
                  { v: "mild", l: "Mild" },
                  { v: "food", l: "Food & lifestyle" },
                ].map((t) => (
                  <TabsTrigger
                    key={t.v}
                    value={t.v}
                    data-testid={`severity-filter-${t.v}`}
                    className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm text-xs"
                  >
                    {t.l}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value={tab} className="mt-5 space-y-3" data-testid="interactions-list">
                {filtered.length === 0 ? (
                  <div className="text-sm p-6 rounded-xl text-center" style={{ background: "#F0FDF4", color: "#166534", border: "1px solid #86EFAC" }}>
                    No {tab === "all" ? "" : tab} concerns detected.
                  </div>
                ) : (
                  filtered.map((i, idx) => (
                    <div
                      key={idx}
                      className="p-4 rounded-2xl border"
                      style={{ borderColor: "#E5DEC9", background: "#FFFFFF" }}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        <SeverityBadge level={i.severity} />
                        <Badge className="rounded-full text-[10px] uppercase" style={{ background: "#F4EFE6", color: "#5C6B66", border: "none" }}>
                          {i.type}
                        </Badge>
                        <span className="font-semibold text-sm" style={{ color: "#1C2826" }}>
                          {(i.items || []).join(" + ")}
                        </span>
                      </div>
                      {i.mechanism ? (
                        <p className="mt-3 text-sm" style={{ color: "#1C2826" }}>
                          <strong>Mechanism:</strong> {i.mechanism}
                        </p>
                      ) : null}
                      {i.effect ? (
                        <p className="mt-1 text-sm" style={{ color: "#5C6B66" }}>
                          <strong>Effect:</strong> {i.effect}
                        </p>
                      ) : null}
                      {i.recommendation ? (
                        <p className="mt-2 text-sm rounded-lg p-2" style={{ background: "#F4EFE6", color: "#23473C" }}>
                          <strong>Advice:</strong> {i.recommendation}
                        </p>
                      ) : null}
                    </div>
                  ))
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* SYMPTOMS */}
          <div className="card-warm p-6">
            <h2 className="font-serif-display text-2xl font-medium mb-4" style={{ color: "#1C2826" }}>
              Possible symptoms to watch
            </h2>
            {(s.potential_symptoms || []).length === 0 ? (
              <p className="text-sm" style={{ color: "#5C6B66" }}>No notable symptoms flagged.</p>
            ) : (
              <ul className="space-y-2" data-testid="symptoms-list">
                {s.potential_symptoms.map((sym, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-3 p-3 rounded-xl border"
                    style={{ borderColor: "#E5DEC9" }}
                  >
                    <span
                      className="mt-1 w-2 h-2 rounded-full shrink-0"
                      style={{
                        background:
                          sym.likelihood === "high"
                            ? "#DC2626"
                            : sym.likelihood === "moderate"
                            ? "#D97706"
                            : "#16A34A",
                      }}
                    />
                    <div>
                      <p className="text-sm font-semibold" style={{ color: "#1C2826" }}>
                        {sym.symptom}
                        <span className="ml-2 text-xs font-normal" style={{ color: "#5C6B66" }}>
                          ({sym.likelihood} likelihood)
                        </span>
                      </p>
                      {sym.cause ? (
                        <p className="text-xs" style={{ color: "#5C6B66" }}>
                          Related to: {sym.cause}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* FOOD FLAGS */}
          {(s.food_alcohol_flags || []).length > 0 && (
            <div className="card-warm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Utensils size={18} style={{ color: "#BF5832" }} />
                <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
                  Food, alcohol &amp; lifestyle flags
                </h2>
              </div>
              <ul className="space-y-2" data-testid="food-flags-list">
                {s.food_alcohol_flags.map((f, i) => (
                  <li
                    key={i}
                    className={`p-3 rounded-xl border severity-${f.severity}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <SeverityBadge level={f.severity} />
                      <span className="font-semibold text-sm" style={{ color: "#1C2826" }}>{f.factor}</span>
                    </div>
                    <p className="mt-1 text-sm" style={{ color: "#5C6B66" }}>{f.concern}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* RECOMMENDATIONS */}
          {(s.recommendations || []).length > 0 && (
            <div className="card-warm p-6" style={{ background: "#E8F0EC", borderColor: "#2D5A4C" }}>
              <div className="flex items-center gap-2 mb-3">
                <Info size={18} style={{ color: "#23473C" }} />
                <h2 className="font-serif-display text-2xl font-medium" style={{ color: "#1C2826" }}>
                  Smart next steps
                </h2>
              </div>
              <ul className="space-y-2" data-testid="recommendations-list">
                {s.recommendations.map((r, i) => (
                  <li key={i} className="text-sm flex gap-2" style={{ color: "#23473C" }}>
                    <span>•</span>
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-5">
                <Link to="/dashboard">
                  <Button className="btn-brand rounded-full" data-testid="report-back-btn">
                    Back to dashboard
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
